import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Logo } from 'components/Logo';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { useLanguage } from 'lib/i18n';
import { LanguageSwitcher } from 'components/LanguageSwitcher';
import { Lock, Loader2 } from 'lucide-react';

const MIN_PASSWORD_LENGTH = 6;

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError(t('auth.resetInvalid'));
      setChecking(false);
      return;
    }
    let cancelled = false;
    api.get('/auth/reset-password/validate', { params: { token } })
      .then(r => { if (!cancelled) setEmail(r.data?.email || ''); })
      .catch(err => { if (!cancelled) setTokenError(getApiErrorMessage(err, t('auth.resetExpired'))); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(t('auth.passwordLength'));
      return;
    }
    if (password !== confirmation) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      toast.success(t('auth.resetSuccess'));
      navigate('/login');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('auth.resetFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between"><Logo /><LanguageSwitcher /></div>
        <h1 className="text-3xl font-bold mb-2">{t('auth.newPassword')}</h1>

        {checking ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="animate-spin" size={16} /> {t('auth.checking')}
          </div>
        ) : tokenError ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200" data-testid="reset-error">
              {tokenError}
            </div>
            <Link to="/forgot-password">
              <Button variant="outline" className="w-full">{t('auth.requestNewLink')}</Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="text-muted-foreground mb-6">
              {t('auth.choosePasswordFor')} {email}.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="password" type="password" required minLength={MIN_PASSWORD_LENGTH} value={password} onChange={e => setPassword(e.target.value)} className="pl-9" data-testid="reset-password-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="confirmation">{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="confirmation" type="password" required minLength={MIN_PASSWORD_LENGTH} value={confirmation} onChange={e => setConfirmation(e.target.value)} className="pl-9" data-testid="reset-confirmation-input" />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
                {loading ? <Loader2 className="animate-spin" size={16} /> : t('auth.reset')}
              </Button>
            </form>
          </>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/login" className="text-primary hover:underline">{t('auth.backLogin')}</Link>
        </p>
      </div>
    </div>
  );
}
